import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { ErrorResponseHandlerService } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.service";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { DwdmCircuitDiagnosticData, DwdmCircuitInfo, DwdmCircuitViewData } from "../dwdm-circuit.model";
import { MuxDemuxData, SlotData } from "../wizard/steps/1-ends/end-step-circuit-dwdm.model";
import { ANGULARJS_TRANSLATE, LOADING_PANE_SERVICE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";

/**
 * Serviço responsável pela gestão de circuitos DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class CircuitService {
    protected readonly baseUrl: string;

    constructor(
        protected readonly http: HttpClient,
        protected readonly errorResponseHandlerService: ErrorResponseHandlerService,
        protected readonly nmsDialogService: NmsDialogService,
        @Inject(LOADING_PANE_SERVICE) protected paneService: any,
        @Inject(ANGULARJS_TRANSLATE) protected readonly translate: any
    ) {
        this.baseUrl = "/web/topology/circuit/dwdm";
    }

    getSlots(resourceId: number): Observable<SlotData[]> {
        return this.http.get<SlotData[]>(`${this.baseUrl}/options/slots/${resourceId}`).pipe(
            map((slotsData) => {
                slotsData.forEach((slot) =>
                    slot.ports.forEach((port) => {
                        port.chassisNo = slot.chassisNo;
                        port.slotNo = slot.slotNo;
                    })
                );
                return slotsData;
            }),
            catchError((error) => {
                this.handleError(error);
                return of([]);
            })
        );
    }

    getMuxes(resourceId: number): Observable<MuxDemuxData[]> {
        return this.http.get<MuxDemuxData[]>(`${this.baseUrl}/options/muxes/${resourceId}`).pipe(
            catchError((error) => {
                this.handleError(error);
                return of([]);
            })
        );
    }

    getDiagnostic(circuitId: number): Observable<DwdmCircuitDiagnosticData> {
        return this.http.get<DwdmCircuitDiagnosticData>(`${this.baseUrl}/diagnostic/${circuitId}`).pipe(
            catchError((error) => {
                this.handleError(error);
                return of(new DwdmCircuitDiagnosticData());
            })
        );
    }

    removeFromNms(circuitsToRemove: DwdmCircuitInfo[]): Observable<any> {
        const itensToRemove = circuitsToRemove.map((circuito) => circuito.resourceId);

        const httpOptions = {
            headers: new HttpHeaders({ "Content-Type": "application/json" }),
            body: itensToRemove
        };

        return this.http.delete<any>(`${this.baseUrl}/remove-from-nms`, httpOptions).pipe(
            catchError((error) => {
                this.paneService.closeLoadingPane();
                this.handleError(error, "dwdm.circuit.table.item.fail.to.remove.title");
                return of(false);
            })
        );
    }

    removeAndActivate(circuitToRemove: DwdmCircuitInfo): Observable<any> {
        return this.http.delete<any>(`${this.baseUrl}/remove-and-activate/${circuitToRemove.resourceId}`).pipe(
            catchError((error) => {
                this.paneService.closeLoadingPane();
                 this.handleError(error, "dwdm.circuit.table.item.fail.to.remove.title");
                return of(false);
            })
        );
    }

    retrieveDeviceNames(circuitInfo: DwdmCircuitInfo): Observable<Array<string>> {
        return this.http.get<string[]>(`${this.baseUrl}/retrieve/device-names/${circuitInfo.resourceId}`).pipe(
            catchError((error) => {
                this.handleError(error);
                return of([]);
            })
        );
    }

    loadCircuitById(circuitId: number): Observable<DwdmCircuitViewData> {
        return this.http.get<DwdmCircuitViewData>(`${this.baseUrl}/${circuitId}`).pipe(
            catchError((error) => {
                this.handleError(error);
                return of(new DwdmCircuitViewData());
            })
        );
    }

    private handleError(error: HttpErrorResponse, titleKey?: string): void {
        const { description, details } = this.errorResponseHandlerService.buildErrorDescriptionDetails(error.error);

        const dialogData = titleKey
            ? { title: this.translate.instant(titleKey), description: description + details, acceptLabel: "OK", bindHtml: true }
            : { description: description + details, acceptLabel: "OK", bindHtml: true }

        this.nmsDialogService.openDialog(dialogData, { maxWidth: "800px" });
    }
}
